import { Injectable } from '@angular/core';
import { CurrentSiteRole } from '@app/shared/models/authentication/session';
import { lastValueFrom, ReplaySubject } from 'rxjs';
import {
    GetMeQuotasResponse,
    LoginOrganizationSite,
    LoginResponse,
    OrganizationSite,
    UserRoles,
} from 'api/models';
import { StorageService } from '../storage-service/storage.service';
import { Role } from '@app/shared/models/authentication/role';
import { AuthenticationService, UsersService } from 'api/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { Router } from '@angular/router';
import { AuthService } from '@app/core/services/auth.service';

@Injectable({
    providedIn: 'root',
})
export class SessionService {
    public currentSiteRole$: ReplaySubject<CurrentSiteRole> = new ReplaySubject(
        1
    );
    public userSession$: ReplaySubject<LoginResponse | null> =
        new ReplaySubject(1);
    private userSession: LoginResponse | null;

    public currentOrganizationSite$: ReplaySubject<LoginOrganizationSite> =
        new ReplaySubject(1);
    public organizationSite: LoginOrganizationSite;
    public currentOrganizationKey: string;

    private organizationSites: OrganizationSite[] = [];
    public quotas: GetMeQuotasResponse;

    constructor(
        private storageService: StorageService,
        private usersService: UsersService,
        private spinner: NgxSpinnerService,
        private router: Router,
        private authenticationService: AuthenticationService,
        private authService: AuthService
    ) {
        let currentSiteRole = this.storageService.getItem('currentSiteRole');
        if (currentSiteRole) {
            currentSiteRole = JSON.parse(currentSiteRole);
            this.currentSiteRole$.next(currentSiteRole);
        }

        this.userSession$.subscribe(session => {
            if (session) {
                this.userSession = session;
            } else this.userSession = null;
        });
    }

    public getMyOrganizationSites() {
        return this.organizationSites;
    }

    public get isServiceProvider() {
        return !this.userSession?.organizations.length;
    }

    public get isOrganizationAdmin() {
        return this.userSession?.roles.includes(UserRoles.OrganizationAdmin);
    }

    public get isSiteAdmin() {
        return this.userSession?.roles.includes(UserRoles.SiteAdmin);
    }

    public getUserRole(
        site: LoginOrganizationSite | OrganizationSite
    ):
        | UserRoles.Carrier
        | UserRoles.Consignee
        | UserRoles.Consignor
        | UserRoles.RemoteConsignor
        | UserRoles.RemoteConsignee
        | UserRoles.RemoteCarrier
        | undefined {
        if (site.consignee && site.roles?.includes(UserRoles.Consignee)) {
            return UserRoles.Consignee;
        }
        if (site.consignee && site.roles?.includes(UserRoles.RemoteConsignee)) {
            return UserRoles.RemoteConsignee;
        }
        if (site.consignor && site.roles?.includes(UserRoles.Consignor)) {
            return UserRoles.Consignor;
        }
        if (site.consignor && site.roles?.includes(UserRoles.RemoteConsignor)) {
            return UserRoles.RemoteConsignor;
        }
        if (site.carrier && site.roles?.includes(UserRoles.Carrier)) {
            return UserRoles.Carrier;
        }
        if (site.carrier && site.roles?.includes(UserRoles.RemoteCarrier)) {
            return UserRoles.RemoteCarrier;
        }
        return undefined;
    }

    public isConsignee(site: LoginOrganizationSite | OrganizationSite) {
        return (
            site.consignee &&
            (site.roles?.includes(UserRoles.Consignee) ||
                site.roles?.includes(UserRoles.RemoteConsignee))
        );
    }

    public isConsignor(site: LoginOrganizationSite | OrganizationSite) {
        return (
            site.consignor &&
            (site.roles?.includes(UserRoles.Consignor) ||
                site.roles?.includes(UserRoles.RemoteConsignor))
        );
    }

    public isCarrier(site: LoginOrganizationSite | OrganizationSite) {
        return (
            site.carrier &&
            (site.roles?.includes(UserRoles.Carrier) ||
                site.roles?.includes(UserRoles.RemoteCarrier))
        );
    }

    public async getFreshSiteInfos(siteKey: string) {
        const me = await lastValueFrom(this.usersService.getMe());
        const site = me.organizationSites.find(site => site._key === siteKey);

        if (!site) return null;

        return site;
    }

    // user is site admin but nothing else on the site
    public isOnlySiteAdmin(
        currentSite: LoginOrganizationSite | OrganizationSite
    ) {
        let isOnlySiteAdmin = this.userSession?.roles.includes(
            UserRoles.SiteAdmin
        );

        if (this.isCarrier(currentSite)) return false;
        if (this.isConsignee(currentSite)) return false;
        if (this.isConsignor(currentSite)) return false;

        return isOnlySiteAdmin;
    }

    /**
     * Removes the session from the localStorage of the browser
     */
    public deleteSession(): void {
        console.log('Deleting session...');

        // Clear session storage completely
        sessionStorage.clear();

        // Preserve 'receiveNotifications_' items and language setting
        let itemsToKeep: { [key: string]: string } = {};
        const prefix = 'receiveNotifications_';

        // preserve language setting
        let lng;
        if (this.storageService.itemExists('language_code')) {
            lng = this.storageService.getItemAsObject('language_code');
        }

        // Iterate through all localStorage items and preserve notification settings
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            if (key && key.startsWith(prefix)) {
                itemsToKeep[key] = localStorage.getItem(key) as string;
            }
        }

        // Clear all localStorage
        localStorage.clear();

        // Restore preserved items
        if (lng) {
            this.storageService.setItem('language_code', lng);
        }
        for (const key in itemsToKeep) {
            localStorage.setItem(key, itemsToKeep[key]);
        }

        // Clear all authentication-related data
        this.storageService.removeItem('sessionId');
        this.storageService.removeItem('currentSiteRole');
        this.storageService.removeItem('returnUrl');

        // Reset internal state
        this.userSession = null;
        this.organizationSite = null as any;
        this.currentOrganizationKey = '';
        this.organizationSites = [];
        this.quotas = null as any;

        // Notify all subscribers that session is null
        this.userSession$.next(null);
        this.currentSiteRole$.next(null as any);
        this.currentOrganizationSite$.next(null as any);

        console.log('Session deleted successfully');
    }

    public orgSiteTypeFlagToRole(organizationSite: LoginOrganizationSite) {
        if (organizationSite.consignor) {
            return Role.CONSIGNOR;
        } else if (organizationSite.consignee) {
            return Role.CONSIGNEE;
        } else if (organizationSite.carrier) {
            return Role.CARRIER;
        }
        return Role.CONSIGNOR;
    }

    public sessionExists(): boolean {
        return this.userSession !== null;
    }

    public async isLoggedIn() {
        try {
            return this.authService.isAuthenticated();
        } catch (error) {
            return false;
        }
    }

    public getSession(): LoginResponse {
        return this.userSession as LoginResponse;
    }

    public async loadUserFromApi() {
        try {
            const user = await lastValueFrom(this.usersService.getMe());

            // Sort organization sites: non-remote first, remote last
            if (user?.organizationSites) {
                user.organizationSites = this.sortSitesByRemoteStatus(
                    user.organizationSites
                );
            }

            this.userSession$.next(user);

            if (user) {
                this.organizationSites = await lastValueFrom(
                    this.usersService.getAccessibleUserSites()
                );
            }

            if (user?.organizationSites?.length > 0) {
                const currentSiteRole =
                    this.storageService.getItem('currentSiteRole');
                const parsedSiteRole = currentSiteRole
                    ? JSON.parse(currentSiteRole)
                    : null;
                const currentSite = parsedSiteRole
                    ? user.organizationSites.find(
                          site => site._key === parsedSiteRole.siteInfo._key
                      )!
                    : user.organizationSites[0];

                this.currentSiteRole$.next(parsedSiteRole);
                this.currentOrganizationSite$.next(currentSite);
                this.organizationSite = currentSite;
                this.currentOrganizationKey = currentSite._key;
            }
        } catch (error) {
            throw error;
        }
    }

    public getSessionId() {
        const currentPath = this.router.url;
        const isOnSelfservice = currentPath.startsWith('/selfservice');

        // For selfservice routes, always use localStorage for permanent sessions
        if (isOnSelfservice) {
            const sessionId = this.storageService.getItem('sessionIdPermanent');
            if (sessionId) {
                return sessionId.replaceAll('"', '');
            }
        }
        return null;
    }

    async getQuotas(
        force: boolean = false
    ): Promise<GetMeQuotasResponse | undefined> {
        if (!force) {
            if (this.quotas) return this.quotas;
        }

        try {
            const quotas = await lastValueFrom(this.usersService.getMeQuotas());
            this.quotas = quotas;
        } catch (error) {}
        return this.quotas;
    }

    /**
     * Checks whether the authenticated user has the given role.
     * @param role
     */
    public authorizedUserHasRole(role: Role): boolean {
        const allSites = this.getSession()?.organizationSites;
        if (allSites?.length > 0) {
            const site = allSites.find(
                org => org._key === this.currentOrganizationSiteKey()
            );

            if (site?.consignor && role === Role.CONSIGNOR) {
                return true;
            }

            if (site?.consignee && role === Role.CONSIGNEE) {
                return true;
            }

            if (site?.carrier && role === Role.CARRIER) {
                return true;
            }
        }

        return false;
    }

    /**
     * change current organization sitekey
     */
    public setCurrentOrganizationSite(organizationSiteKey: string) {
        const currentOrganizationSite =
            this.getSession().organizationSites.filter(
                org => org._key === organizationSiteKey
            );

        this.currentOrganizationSite$.next(currentOrganizationSite[0]);
        this.organizationSite = currentOrganizationSite[0];
        this.currentOrganizationKey = currentOrganizationSite[0]._key;
    }

    public currentOrganizationSiteKey() {
        return this.currentOrganizationKey;
    }

    public currentOrganizationSite() {
        return this.organizationSite;
    }

    public async logout() {
        this.authService.logout();
        this.deleteSession();
    }

    public async logoutAfterPasswordChange() {
        await this.spinner.show();
        try {
            this.deleteSession();
        } catch (error) {}
        await this.router.navigateByUrl(RoutingEndpoints.login);
        location.reload();
    }

    /**
     * Checks if a site is a remote site (read-only access)
     */
    private isRemoteSite(site: LoginOrganizationSite): boolean {
        return site.roles?.some(
            role =>
                role === UserRoles.RemoteConsignor ||
                role === UserRoles.RemoteConsignee ||
                role === UserRoles.RemoteCarrier
        );
    }

    /**
     * Sorts sites so non-remote sites appear first, remote sites last.
     * Preserves original order within each group.
     */
    private sortSitesByRemoteStatus(
        sites: LoginOrganizationSite[]
    ): LoginOrganizationSite[] {
        return [...sites].sort((a, b) => {
            const aIsRemote = this.isRemoteSite(a);
            const bIsRemote = this.isRemoteSite(b);

            if (aIsRemote === bIsRemote) return 0;
            return aIsRemote ? 1 : -1;
        });
    }
}
